import React, { useState, useEffect, useRef } from 'react';
import { Play, Info, Volume2, VolumeX, Eye, Heart, MessageSquare } from 'lucide-react';

const formatCount = (count?: number) => {
  if (count === undefined || count === null || isNaN(count)) return '0';
  if (count >= 1000000) return (count / 1000000).toFixed(1).replace('.', ',') + 'M';
  if (count >= 1000) return (count / 1000).toFixed(1).replace('.', ',') + ' mil';
  return count.toString();
};

const extractYouTubeId = (urlOrId?: string): string => {
  if (!urlOrId) return '';
  const trimmed = String(urlOrId).trim();
  if (trimmed.length === 11 && !trimmed.includes('/') && !trimmed.includes('.')) return trimmed;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = trimmed.match(regExp);
  return (match && match[2].length === 11) ? match[2] : '';
};

export default function HeroBannerMobile({ videos, onPlay, onInfo }: { videos: any[], onPlay: (id: string) => void, onInfo: (id: string) => void }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [showVideo, setShowVideo] = useState(false);

  // Estados para detectar o gesto de arrastar com o dedo (Swipe)
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const bannerVideos = videos.slice(0, 6);

  const handlePrev = () => setCurrentIndex((prev) => (prev === 0 ? bannerVideos.length - 1 : prev - 1));
  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % bannerVideos.length);

  // Funções de controle do Swipe (Arrastar com o dedo)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX; // 👈 ESSA LINHA ZERA A CONTA NO TOQUE RÁPIDO!
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Distância mínima em pixels para considerar um swipe

    if (distance > minSwipeDistance) {
      // Arrastou para a esquerda -> Próximo banner
      handleNext();
    } else if (distance < -minSwipeDistance) {
      // Arrastou para a direita -> Banner anterior
      handlePrev();
    }
  };

  // Troca automática a cada 60 segundos
  useEffect(() => {
    if (bannerVideos.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % bannerVideos.length);
    }, 60000);
    return () => clearInterval(timer);
  }, [bannerVideos.length]);

  // Mostrar capa por 5 segundos antes do vídeo
  useEffect(() => {
    setShowVideo(false);
    const timer = setTimeout(() => {
      setShowVideo(true);
      setIsMuted(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  // Gerenciamento de visibilidade da aba
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            '*'
          );
        }
      } else {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            '*'
          );
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const toggleMute = () => {
    if (!iframeRef.current || !iframeRef.current.contentWindow) return;

    if (isMuted) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'unMute', args: [] }),
        '*'
      );
      setIsMuted(false);
    } else {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'mute', args: [] }),
        '*'
      );
      setIsMuted(true);
    }
  };

  if (bannerVideos.length === 0) return null;

  const currentVideo = bannerVideos[currentIndex] || {};
  const videoUrl = currentVideo.embed_id || currentVideo.full_video_id || currentVideo.trailer_url || currentVideo.url;
  const ytId = extractYouTubeId(videoUrl);

  return (
    <div 
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ position: 'relative', width: '100%', backgroundColor: '#000000', overflow: 'hidden', paddingBottom: '30px', touchAction: 'pan-y' }}
    >
      
      {/* Vídeo / Capa no Topo com 5 segundos iniciais de capa e suporte a gesto */}
      <div style={{ position: 'relative', width: '100%', height: '230px', backgroundColor: '#111' }}>
        {ytId && showVideo ? (
          <iframe
            ref={iframeRef}
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&loop=1&playlist=${ytId}&end=60&modestbranding=1&enablejsapi=1`}
            style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }}
            allow="autoplay; encrypted-media"
            title="Mobile Video"
          />
        ) : (
          <img src={currentVideo.thumbnail} alt="Capa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}

        {/* ❌ AS SETAS FORAM REMOVIDAS DAQUI. AGORA É SÓ ARRASTAR COM O DEDO! */}

        {/* Botão de Som (só aparece quando o vídeo estiver rodando) */}
        {ytId && showVideo && (
          <button
            onClick={toggleMute}
            style={{
              position: 'absolute', bottom: '12px', right: '12px', zIndex: 30,
              backgroundColor: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', borderRadius: '50%', width: '36px', height: '36px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', pointerEvents: 'auto'
            }}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        )}

        {/* Degradê suave embaixo do vídeo */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '40px', background: 'linear-gradient(to top, #000000, transparent)', pointerEvents: 'none' }} />
      </div>

      {/* Informações e Botões */}
      <div style={{ padding: '12px 16px 0 16px', textAlign: 'center' }}>
        <h4 style={{ color: '#E50914', margin: '0 0 4px 0', fontSize: '10px', fontWeight: '800', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
          TRAILER EM DESTAQUE
        </h4>
        <h2 style={{ fontSize: '17px', fontWeight: '800', color: '#fff', margin: '0 0 6px 0', lineHeight: '1.2' }}>
          {currentVideo.title}
        </h2>

        {/* Descrição curta limitada a 2 linhas para o Mobile */}
        <p style={{ 
          fontSize: '12px', color: '#ccc', margin: '0 auto 10px auto', 
          maxWidth: '90%', lineHeight: '1.4',
          display: '-webkit-box', 
          WebkitLineClamp: 2, 
          WebkitBoxOrient: 'vertical', 
          overflow: 'hidden' 
        }}>
          {currentVideo.description || currentVideo.sinopse || 'Assista ao conteúdo completo e dezenas de curiosidades na plataforma Doisplay.'}
        </p>

        {/* Métricas de Engajamento */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '14px', color: '#bbb', fontSize: '12px', fontWeight: '600' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Eye size={14} color="#aaa" /> {formatCount(currentVideo.views_count || 0)}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Heart size={14} color="#E50914" fill="#E50914" /> {formatCount(currentVideo.likes_count || 0)}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MessageSquare size={14} color="#3b82f6" /> {formatCount(currentVideo.comments_count || 0)}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '16px' }}>
          <button 
            onClick={() => onPlay(currentVideo.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', 
              backgroundColor: '#fff', color: '#000', border: 'none', 
              padding: '10px 22px', borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer'
            }}>
            <Play size={14} fill="#000" /> Reproduzir
          </button>
          <button 
            onClick={() => onInfo(currentVideo.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', 
              padding: '10px 18px', borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer'
            }}>
            <Info size={14} /> Detalhes
          </button>
        </div>

        {/* Indicadores / Pontinhos Mobile */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
          {bannerVideos.map((_, idx) => (
            <div 
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              style={{
                width: idx === currentIndex ? '16px' : '5px',
                height: '5px', borderRadius: '3px',
                backgroundColor: idx === currentIndex ? '#E50914' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer'
              }}
            />
          ))}
        </div>
      </div>

    </div>
  );
}